import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Layout } from '@/components/layout/Layout';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Repositories from '@/pages/Repositories';
import RepositoryDetail from '@/pages/RepositoryDetail';
import Users from '@/pages/Users';
import Projects from '@/pages/Projects';
import Activity from '@/pages/Activity';
import Alerts from '@/pages/Alerts';
import Reviews from '@/pages/Reviews';
import Settings from '@/pages/Settings';
import Reports from '@/pages/Reports';
import ProjectDashboard from '@/pages/ProjectDashboard';
import Performance from '@/pages/Performance';
import Jobs from '@/pages/Jobs';
import UserManagement from '@/pages/UserManagement';
import AICosts from '@/pages/AICosts';
import { UserRole } from '@/types';
import Board from '@/pages/Board';
import Overview from '@/pages/Overview';
import Servers from '@/pages/Servers';
import IssueMemoryPage from '@/pages/IssueMemory';
import AccessTokens from '@/pages/AccessTokens';
import ShadowReview from '@/pages/ShadowReview';
import Agents from '@/pages/Agents';
import ProjectSetup from '@/pages/ProjectSetup';

const MEMORY_ROLES: UserRole[] = ['executive', 'pm', 'developer', 'support'];
// Who sees what (backend access/scope.ts): the CEO everything; the PM all but AI cost and access tokens; everyone
// else the board, their projects and repos and those projects' AI sessions (the API limits it to their GitLab projects)
const LEADS: UserRole[] = ['executive', 'pm'];

/** The home page: the dashboard for the CEO and the PM, the board for everyone else */
function Home() {
  const { user } = useAuth();
  return user && LEADS.includes(user.role) ? <Dashboard /> : <Navigate to="/board" replace />;
}

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />

            {/* Protected routes */}
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Home />} />
              <Route
                path="/users"
                element={
                  <ProtectedRoute allowedRoles={['executive', 'pm']}>
                    <Users />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-management"
                element={
                  <ProtectedRoute allowedRoles={['executive']}>
                    <UserManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/servers"
                element={
                  <ProtectedRoute allowedRoles={LEADS}>
                    <Servers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/overview"
                element={
                  <ProtectedRoute allowedRoles={LEADS}>
                    <Overview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/board"
                element={
                  <ProtectedRoute allowedRoles={MEMORY_ROLES}>
                    <Board />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/issues/:project/:iid"
                element={
                  <ProtectedRoute allowedRoles={MEMORY_ROLES}>
                    <IssueMemoryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/access-tokens"
                element={
                  <ProtectedRoute allowedRoles={['executive']}>
                    <AccessTokens />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shadow-review"
                element={
                  <ProtectedRoute allowedRoles={['executive']}>
                    <ShadowReview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agents"
                element={
                  <ProtectedRoute allowedRoles={MEMORY_ROLES}>
                    <Agents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/project-setup"
                element={
                  <ProtectedRoute allowedRoles={['executive', 'pm']}>
                    <ProjectSetup />
                  </ProtectedRoute>
                }
              />
              <Route path="/projects" element={<Projects />} />
              <Route
                path="/projects/:projectId"
                element={
                  <ProtectedRoute allowedRoles={MEMORY_ROLES}>
                    <ProjectDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/repos"
                element={
                  <ProtectedRoute allowedRoles={MEMORY_ROLES}>
                    <Repositories />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/repos/:id"
                element={
                  <ProtectedRoute allowedRoles={MEMORY_ROLES}>
                    <RepositoryDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/activity"
                element={
                  <ProtectedRoute allowedRoles={LEADS}>
                    <Activity />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alerts"
                element={
                  <ProtectedRoute allowedRoles={LEADS}>
                    <Alerts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reviews"
                element={
                  <ProtectedRoute allowedRoles={['executive', 'pm']}>
                    <Reviews />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute allowedRoles={['executive', 'pm']}>
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/performance"
                element={
                  <ProtectedRoute allowedRoles={['executive', 'pm']}>
                    <Performance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute allowedRoles={['executive', 'pm']}>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jobs"
                element={
                  <ProtectedRoute allowedRoles={['executive']}>
                    <Jobs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-costs"
                element={
                  <ProtectedRoute allowedRoles={['executive']}>
                    <AICosts />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
